import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDown, ArrowDownToLine, ArrowDownUp, ArrowUp, ArrowUpFromLine, Bitcoin,
  ChevronDown, CreditCard, Edit3, Eye, EyeOff, Image as ImageIcon, Info,
  Landmark, LayoutGrid, ListOrdered, Plug, Plus, RefreshCw, Rows3, Save,
  Search, ShieldCheck, SlidersHorizontal, Trash2, Upload, Wallet, X,
} from 'lucide-react';
import client from '../lib/api';
import { UPLOAD_MAX_LABEL, uploadTooLarge } from '../lib/mediaAsset';
import PaymentMethodLogo, { resolvePaymentBrand } from '../components/PaymentMethodLogo';
import { CRYPTO_ASSETS, cryptoAssetLabel, findCryptoAsset } from '../constants/cryptoAssets';
// Bölüm (havale / kredi kartı / kripto / e-cüzdan) oyuncu ekranlarıyla aynı
// yerden çözülür: sunucu `category` alanını gönderir, istemci yalnızca sırayı
// ve adı bilir. Panelde ikinci bir gruplama yazılsaydı yönetici yöntemi
// panelde bir bölümde, oyuncu başka bölümde görürdü.
import { DEPOSIT_CATEGORIES, categoryOf } from '../lib/paymentCategories';
// Varsayılan uyarı metni tek yerdedir: panel onu ipucu ve yer tutucu olarak
// gösterir, oyuncu ekranları ve sunucu aynı metni çizer.
import { SHOWCASE_UNAVAILABLE_MESSAGE as SHOWCASE_DEFAULT_MESSAGE } from '../lib/showcaseMethod';
import { useToast } from '../hooks/use-toast';
import { AdminEmpty, AdminError, AdminSkeleton } from './components/PageState';
import './paymentMethods.css';

const emptyForm = {
  name: '',
  provider: 'manual',
  method_type: 'bank',
  direction: 'deposit',
  icon_url: '',
  bank_name: '',
  account_holder: '',
  iban: '',
  asset: '',
  network: '',
  wallet_address: '',
  instructions: '',
  min_amount: 100,
  max_amount: 100000,
  showcase_only: false,
  showcase_message: '',
  active: true,
  order: 0,
};

// Gövdeye yalnızca bu alanlar yazılır. Liste ucu `id`, `category`,
// `created_at` gibi türev alanları da döndürür ve formu doldururken onlar da
// kopyalanır; hepsini geri göndermek sunucunun doğrulamasına yabancı alan
// taşır (`provider` için 422'ye kadar gider, aşağıya bakın).
const PAYLOAD_KEYS = Object.keys(emptyForm);

const DEFAULT_ASSET = 'USDT';

// Yöntem satırları Finans ayarlarından üretilen sağlayıcılar: panelden
// eklenemez, silinemez ve aktifliği buradan değiştirilemez. Liste backend'deki
// `payment_router.API_PROVIDERS` kümesiyle **aynı kalmalıdır** — eksik kalan
// sağlayıcı panelde manuel yöntem gibi görünür, silme ve durum düğmeleri
// çizilir ve yönetici her denemede 409 alır (HızlıcaParalar bir süre böyleydi).
const API_PROVIDER_LABELS = {
  cashmoney: 'CashMoney Pay',
  paylux: 'Paylux',
  expay: 'ExPay',
  primevpay: 'PrimeVpay',
  wizkasa: 'Wizkasa',
  hemensystem: 'Hemen Havale',
  hera: 'Hera',
  payurus: 'Payurus',
  ultrapay: 'UltraPay',
  linkhavale: 'Link Havale',
  vevopay: 'VEVO PAY',
  hizlicaparalar: 'HızlıcaParalar',
  hizlihavale: 'Hızlı Havale',
};
const isApiManaged = (item) => Boolean(API_PROVIDER_LABELS[item?.provider]);

const DIRECTION_LABELS = {
  deposit: 'Para Yatırma',
  withdrawal: 'Para Çekme',
  both: 'Yatırma ve Çekme',
};

const DIRECTION_SHORT = {
  deposit: 'Yatırma',
  withdrawal: 'Çekme',
  both: 'Yatırma + Çekme',
};

const DIRECTION_ICONS = {
  deposit: ArrowDownToLine,
  withdrawal: ArrowUpFromLine,
  both: ArrowDownUp,
};

// Bölüm ikonu ve rengi yalnızca görünümdür; hangi yöntemin hangi bölüme
// düştüğünü sunucu söyler (`services/payment_categories.py`).
const CATEGORY_META = {
  havale: { icon: Landmark, accent: '#4281ed', hint: 'Havale, EFT, FAST' },
  'kredi-karti': { icon: CreditCard, accent: '#a78bfa', hint: 'Kart ile ödeme' },
  kripto: { icon: Bitcoin, accent: '#f59e0b', hint: 'Para birimi ve ağ' },
  'e-cuzdan': { icon: Wallet, accent: '#25cfe8', hint: 'Papara, cüzdan hesapları' },
};

const VIEW_STORAGE_KEY = 'admin:pm-view';
const CLOSED_STORAGE_KEY = 'admin:pm-closed-groups';

// Görünüm ve katlanan bölümler tarayıcıda durur: bir yöneticinin tercihi
// diğerininkini değiştirmemelidir (Herkül'ün sohbet rayıyla aynı yaklaşım).
const readStored = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : raw;
  } catch (storageError) {
    return fallback;
  }
};

const writeStored = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (storageError) {
    /* özel sekmede yazma engellenebilir; tercih kaybolur, sayfa çalışır */
  }
};

const money = (value) => `${Number(value || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})} ₺`;

const maskIban = (iban) => {
  const text = String(iban || '').replace(/\s+/g, '');
  return text.length > 6 ? `${text.slice(0, 4)} •••• ${text.slice(-4)}` : text;
};

const maskWallet = (address) => {
  const text = String(address || '').trim();
  return text.length > 14 ? `${text.slice(0, 7)}…${text.slice(-5)}` : text;
};

/**
 * Yöntemin hesap satırı: üst satır "nerede", alt satır "hangi bilgi".
 * Kart ve liste görünümü aynı metni çizer, ayrışırlarsa aynı yöntem iki
 * görünümde iki farklı şey söylerdi.
 */
const accountSummary = (item) => {
  if (item.showcase_only) {
    return { main: 'Hesap yok', sub: 'Üye ödeme adımında uyarı görür' };
  }
  if (item.method_type === 'wallet') {
    return { main: 'Sağlayıcı cüzdanı', sub: 'Cüzdan numarasını üye girer' };
  }
  if (item.method_type === 'crypto') {
    const main = item.network || item.asset || 'Kripto';
    if (item.direction === 'withdrawal') {
      return { main, sub: 'Cüzdan adresini üye girer' };
    }
    if (isApiManaged(item)) {
      return { main, sub: 'Adresi sağlayıcı her işlemde verir' };
    }
    return {
      main,
      sub: item.wallet_address ? maskWallet(item.wallet_address) : 'Adres girilmemiş',
      mono: Boolean(item.wallet_address),
    };
  }
  const main = item.bank_name || 'Banka hesabı';
  if (item.direction === 'withdrawal') {
    return { main, sub: "IBAN'ı üye girer" };
  }
  if (isApiManaged(item)) {
    return { main, sub: 'Hesabı sağlayıcı her işlemde verir' };
  }
  return {
    main,
    sub: item.iban ? maskIban(item.iban) : 'IBAN girilmemiş',
    mono: Boolean(item.iban),
  };
};

const matchesSearch = (item, needle) => {
  if (!needle) return true;
  const haystack = [
    item.name, item.bank_name, item.account_holder, item.iban, item.asset,
    item.network, item.wallet_address, API_PROVIDER_LABELS[item.provider],
    DIRECTION_LABELS[item.direction],
  ].filter(Boolean).join(' ').toLocaleLowerCase('tr-TR');
  return haystack.includes(needle);
};

const PaymentMethods = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [iconBusy, setIconBusy] = useState(false);
  const [iconOpen, setIconOpen] = useState(false);
  const [ordering, setOrdering] = useState('');
  // Ad, yönetici alana kendisi yazana kadar seçilen para birimini izler.
  // Metnin otomatik ada eşit olup olmadığına bakmak kırılgandı: yönetici tek
  // harf düzeltince ad para biriminden kopuyordu.
  const [nameTouched, setNameTouched] = useState(false);

  const [search, setSearch] = useState('');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [view, setView] = useState(() => (
    readStored(VIEW_STORAGE_KEY, 'grid') === 'list' ? 'list' : 'grid'
  ));
  const [closedGroups, setClosedGroups] = useState(() => {
    const raw = readStored(CLOSED_STORAGE_KEY, '');
    return new Set(raw ? raw.split(',').filter(Boolean) : []);
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get('/admin/payment-methods');
      setItems(data.items || []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Ödeme yöntemleri alınamadı.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const chooseView = (next) => {
    setView(next);
    writeStored(VIEW_STORAGE_KEY, next);
  };

  const toggleGroup = (categoryId) => setClosedGroups((current) => {
    const next = new Set(current);
    if (next.has(categoryId)) next.delete(categoryId);
    else next.add(categoryId);
    writeStored(CLOSED_STORAGE_KEY, [...next].join(','));
    return next;
  });

  const openNew = () => {
    setForm(emptyForm);
    setIconOpen(false);
    setNameTouched(false);
    setEditing({ mode: 'new' });
  };

  const openEdit = (item) => {
    setForm({ ...emptyForm, ...item });
    // Kayıtlı ad yöneticinin tercihidir; para birimi değişse de korunur.
    setNameTouched(Boolean(item.name));
    setIconOpen(Boolean(item.icon_url));
    setEditing({ mode: 'edit', id: item.id });
  };

  // Tür değişince yalnızca o türe ait alanlar dolu kalır; kripto seçilince
  // makul bir para birimi ve ağ önerilir, banka seçilince kripto alanları
  // temizlenir.
  const chooseType = (methodType) => setForm((current) => {
    if (current.method_type === methodType) return current;
    if (methodType === 'bank') {
      return { ...current, method_type: 'bank', asset: '', network: '', wallet_address: '' };
    }
    const asset = current.asset || DEFAULT_ASSET;
    return {
      ...current,
      method_type: 'crypto',
      asset,
      network: current.network || findCryptoAsset(asset)?.networks?.[0] || '',
      name: nameTouched ? current.name : cryptoAssetLabel(asset),
      bank_name: '',
      account_holder: '',
      iban: '',
    };
  });

  const chooseAsset = (symbol) => setForm((current) => {
    const networks = findCryptoAsset(symbol)?.networks || [];
    const keepNetwork = networks.includes(current.network);
    return {
      ...current,
      asset: symbol,
      network: keepNetwork ? current.network : (networks[0] || ''),
      name: nameTouched ? current.name : cryptoAssetLabel(symbol),
    };
  });

  const uploadIcon = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({
        title: 'İkon yüklenemedi',
        description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.`,
      });
      return;
    }

    setIconBusy(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await client.post('/admin/payment-methods/icon', body);
      setForm((current) => ({ ...current, icon_url: data.url }));
      toast({ title: 'İkon yüklendi' });
    } catch (requestError) {
      toast({
        title: 'İkon yüklenemedi',
        description: requestError.response?.data?.detail || 'Görsel yüklenemedi.',
      });
    } finally {
      setIconBusy(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = {};
      PAYLOAD_KEYS.forEach((key) => { payload[key] = form[key]; });
      if (editing.mode === 'new') {
        // Yeni kayıt her zaman manueldir: API yöntemlerini sağlayıcı senkronu
        // üretir ve uç serbest `provider` değerini zaten 400 ile reddeder.
        payload.provider = 'manual';
        await client.post('/admin/payment-methods', payload);
      } else {
        // `provider` gövdeye **yazılmaz**: değiştirilemeyen bir alandır ve
        // sunucunun Literal kümesinde bulunmayan bir sağlayıcıda (yeni bir
        // entegrasyon eklendiğinde) bütün kaydı 422'ye düşürürdü — panelde
        // logo yüklemek bile imkânsız hâle gelirdi.
        delete payload.provider;
        await client.patch(`/admin/payment-methods/${editing.id}`, payload);
      }
      toast({ title: 'Ödeme yöntemi kaydedildi' });
      setEditing(null);
      await load();
    } catch (requestError) {
      toast({
        title: 'Kaydedilemedi',
        description: requestError.response?.data?.detail || 'Bir hata oluştu.',
      });
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (item) => {
    try {
      await client.patch(`/admin/payment-methods/${item.id}`, {
        active: !item.active,
      });
      await load();
    } catch (requestError) {
      toast({
        title: 'Durum değiştirilemedi',
        description: requestError.response?.data?.detail,
      });
    }
  };

  const remove = async (item) => {
    // Geçmiş işlemler yöntem adını kendi kaydında tuttuğu için silme onları
    // etkilemez; yöntem yalnızca panelden ve müşteri listesinden kalkar.
    if (!window.confirm(
      `${item.name} silinsin mi? Geçmiş işlemler etkilenmez.`,
    )) return;
    try {
      await client.delete(`/admin/payment-methods/${item.id}`);
      toast({ title: 'Ödeme yöntemi silindi' });
      await load();
    } catch (requestError) {
      toast({
        title: 'Silinemedi',
        description: requestError.response?.data?.detail,
      });
    }
  };

  /**
   * Yöntemi bölümü içinde bir sıra yukarı/aşağı taşır.
   *
   * Taşıma yalnızca **manuel** yöntemlerde vardır: sağlayıcı senkronu
   * (`ensure_payment_methods`) API satırlarının `order` alanını her yazımda
   * yeniden hesaplar, yani orada yapılan bir sıralama bir sonraki ayar
   * kaydında sessizce geri alınırdı.
   *
   * Değerler takas edilmez, **yeniden dağıtılır**: iki yöntemin `order`
   * alanı eşit olabilir (varsayılan 0) ve takas o durumda hiçbir şey
   * değiştirmezdi. Havuz artan hâle getirilip yeni sıraya göre atanır,
   * yalnızca değeri değişen satırlar sunucuya yazılır.
   */
  const moveMethod = async (categoryId, methodId, delta) => {
    const group = items
      .filter((item) => categoryOf(item) === categoryId && !isApiManaged(item));
    const index = group.findIndex((item) => item.id === methodId);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= group.length) return;

    const reordered = [...group];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    const pool = group.map((item) => Number(item.order) || 0).sort((a, b) => a - b);
    for (let i = 1; i < pool.length; i += 1) {
      if (pool[i] <= pool[i - 1]) pool[i] = pool[i - 1] + 1;
    }

    const writes = reordered
      .map((item, position) => ({ item, order: pool[position] }))
      .filter((entry) => Number(entry.item.order) !== entry.order);
    if (!writes.length) return;

    setOrdering(methodId);
    try {
      // Sıra yazımı tek tek yapılır: toplu bir uç yoktur ve komşu takasında
      // pratikte iki satır değişir.
      for (const entry of writes) {
        // eslint-disable-next-line no-await-in-loop
        await client.patch(`/admin/payment-methods/${entry.item.id}`, {
          order: entry.order,
        });
      }
      await load();
    } catch (requestError) {
      toast({
        title: 'Sıra değiştirilemedi',
        description: requestError.response?.data?.detail || 'Bir hata oluştu.',
      });
    } finally {
      setOrdering('');
    }
  };

  // Göstermelik firma vitrinde durur ama arkasında hesap yoktur: banka /
  // cüzdan alanları hiç sorulmaz, ad, logo, limitler ve talimat sorulmaya
  // devam eder. Her iki yönde de tanımlanabilir — yatırımda ödeme adımı,
  // çekimde talep açma reddedilir ve üye aynı uyarıyı görür.
  const isShowcase = Boolean(form.showcase_only);
  const chooseShowcase = (checked) => setForm((current) => ({
    ...current, showcase_only: checked,
  }));

  const isCrypto = form.method_type === 'crypto';
  // Sağlayıcı cüzdanı (Payurus CepPay, UltraPay JoyPayz/Yesod) üçüncü bir
  // türdür ve panelden seçilemez — satırı sağlayıcı üretir. Tür kartlarında
  // "Banka / IBAN" seçili görünüyordu; hedefi bir IBAN değil cüzdan numarası.
  const isWallet = form.method_type === 'wallet';
  const formApiManaged = isApiManaged(form);
  const assetNetworks = findCryptoAsset(form.asset)?.networks || [];

  const stats = useMemo(() => {
    const count = (predicate) => items.filter(predicate).length;
    return {
      total: items.length,
      active: count((item) => item.active),
      passive: count((item) => !item.active),
      deposit: count((item) => item.direction !== 'withdrawal'),
      withdrawal: count((item) => item.direction !== 'deposit'),
      api: count(isApiManaged),
      manual: count((item) => !isApiManaged(item)),
      showcase: count((item) => item.showcase_only),
    };
  }, [items]);

  const needle = search.trim().toLocaleLowerCase('tr-TR');
  const visible = useMemo(() => items.filter((item) => {
    if (directionFilter === 'deposit' && item.direction === 'withdrawal') return false;
    if (directionFilter === 'withdrawal' && item.direction === 'deposit') return false;
    if (statusFilter === 'active' && !item.active) return false;
    if (statusFilter === 'passive' && item.active) return false;
    if (statusFilter === 'api' && !isApiManaged(item)) return false;
    if (statusFilter === 'manual' && isApiManaged(item)) return false;
    if (statusFilter === 'showcase' && !item.showcase_only) return false;
    return matchesSearch(item, needle);
  }), [items, directionFilter, statusFilter, needle]);

  // Bölümler sunucunun çözdüğü kategoriden gelir; boş bölüm hiç çizilmez.
  // Tanınmayan bir kategori sessizce kaybolmamalıdır: `categoryOf` zaten
  // bilinmeyen değeri havaleye düşürür.
  const groups = useMemo(() => DEPOSIT_CATEGORIES
    .map((category) => ({
      ...category,
      ...CATEGORY_META[category.id],
      methods: visible.filter((item) => categoryOf(item) === category.id),
      totalInCategory: items.filter((item) => categoryOf(item) === category.id).length,
    }))
    .filter((group) => group.methods.length > 0), [visible, items]);

  const filtersActive = Boolean(needle)
    || directionFilter !== 'all' || statusFilter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setDirectionFilter('all');
    setStatusFilter('all');
  };

  // Logo isteğe bağlıdır: kapalıyken yalnızca varsayılan ikon ve bir düğme
  // görünür, düğmeye basılınca yükleme alanı açılır. Bileşen olarak değil
  // fonksiyon olarak döner — aksi hâlde her tuşta yeniden bağlanıp dosya
  // seçicinin durumunu sıfırlardı.
  const iconPicker = (description) => (
    <div className="pm-logo-box">
      <PaymentMethodLogo method={form} size={iconOpen ? 52 : 42} />
      {!iconOpen ? (
        <>
          <p className="pm-logo-copy">{description}</p>
          <div className="pm-logo-actions">
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={() => setIconOpen(true)}
            >
              <ImageIcon size={15} />
              Logo yükle
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="pm-logo-copy">PNG, JPG, GIF, WEBP veya SVG. En fazla {UPLOAD_MAX_LABEL}. Hareketli logo için GIF yükleyin.</p>
          <div className="pm-logo-actions">
            <label className="admin-btn admin-btn-secondary cursor-pointer">
              <Upload size={15} />
              {iconBusy
                ? 'Yükleniyor…'
                : (form.icon_url ? 'Logoyu değiştir' : 'Dosya seç')}
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.webp,.svg,image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                className="sr-only"
                disabled={iconBusy}
                onChange={uploadIcon}
              />
            </label>
            {form.icon_url ? (
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => {
                  setForm({ ...form, icon_url: '' });
                  setIconOpen(false);
                }}
              >
                <Trash2 size={15} />
                Varsayılana dön
              </button>
            ) : (
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => setIconOpen(false)}
              >
                Vazgeç
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderCard = (item, group) => {
    const brand = resolvePaymentBrand(item);
    const account = accountSummary(item);
    const managed = isApiManaged(item);
    const DirectionIcon = DIRECTION_ICONS[item.direction] || ArrowDownUp;
    const manualGroup = group.methods.filter((row) => !isApiManaged(row));
    const manualIndex = manualGroup.findIndex((row) => row.id === item.id);

    return (
      <article
        key={item.id}
        className={`pm-card ${item.active ? '' : 'is-off'}`}
        style={{ '--pm-accent': brand.accent }}
      >
        <div className="pm-card-top">
          <span className="pm-card-logo">
            <PaymentMethodLogo method={item} size={38} />
          </span>
          <div className="pm-card-ident">
            <span className="pm-card-name" title={item.name}>{item.name}</span>
            <div className="pm-card-tags">
              <span className={`pm-tag ${item.direction}`}>
                <DirectionIcon size={11} />
                {DIRECTION_SHORT[item.direction] || item.direction}
              </span>
              {managed && (
                <span className="pm-tag api">
                  <Plug size={11} />
                  {API_PROVIDER_LABELS[item.provider]}
                </span>
              )}
              {item.showcase_only && (
                <span className="pm-tag showcase">
                  <EyeOff size={11} />
                  Göstermelik
                </span>
              )}
            </div>
          </div>
        </div>

        <dl className="pm-card-facts">
          <div className="pm-fact">
            <dt>Hesap</dt>
            <dd>
              {account.main}
              <small className={account.mono ? 'pm-mono' : ''}>{account.sub}</small>
            </dd>
          </div>
          <div className="pm-fact">
            <dt>Limit</dt>
            <dd className="pm-limit">
              {money(item.min_amount)} – {money(item.max_amount)}
            </dd>
          </div>
        </dl>

        <div className="pm-card-foot">
          <button
            type="button"
            className={`pm-switch ${item.active ? 'is-on' : ''}`}
            onClick={() => !managed && toggle(item)}
            disabled={managed}
            title={managed
              ? `${API_PROVIDER_LABELS[item.provider]} yöntemi Finans ayarlarından açılıp kapanır`
              : (item.active ? 'Yöntemi kapat' : 'Yöntemi aç')}
          >
            <span className="pm-switch-track" aria-hidden="true" />
            {item.active ? 'Aktif' : 'Pasif'}
          </button>

          <div className="pm-card-tools">
            {!managed && manualGroup.length > 1 && (
              <div className="pm-order" role="group" aria-label={`${item.name} sırası`}>
                <button
                  type="button"
                  onClick={() => moveMethod(group.id, item.id, -1)}
                  disabled={manualIndex <= 0 || Boolean(ordering)}
                  title="Yukarı taşı"
                  aria-label={`${item.name} yöntemini yukarı taşı`}
                >
                  <ArrowUp size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => moveMethod(group.id, item.id, 1)}
                  disabled={manualIndex >= manualGroup.length - 1 || Boolean(ordering)}
                  title="Aşağı taşı"
                  aria-label={`${item.name} yöntemini aşağı taşı`}
                >
                  <ArrowDown size={13} />
                </button>
              </div>
            )}
            <button
              type="button"
              className="pm-tool"
              onClick={() => openEdit(item)}
              title="Düzenle"
              aria-label={`${item.name} yöntemini düzenle`}
            >
              <Edit3 size={15} />
            </button>
            {!managed && (
              <button
                type="button"
                className="pm-tool danger"
                onClick={() => remove(item)}
                title="Sil"
                aria-label={`${item.name} yöntemini sil`}
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        </div>
      </article>
    );
  };

  const renderRow = (item, group) => {
    const brand = resolvePaymentBrand(item);
    const account = accountSummary(item);
    const managed = isApiManaged(item);
    const DirectionIcon = DIRECTION_ICONS[item.direction] || ArrowDownUp;
    const manualGroup = group.methods.filter((row) => !isApiManaged(row));
    const manualIndex = manualGroup.findIndex((row) => row.id === item.id);

    return (
      <div
        key={item.id}
        className={`pm-row ${item.active ? '' : 'is-off'}`}
        style={{ '--pm-accent': brand.accent }}
      >
        <span className="pm-card-logo">
          <PaymentMethodLogo method={item} size={30} />
        </span>
        <div className="pm-row-ident">
          <strong title={item.name}>{item.name}</strong>
          <div className="pm-card-tags">
            <span className={`pm-tag ${item.direction}`}>
              <DirectionIcon size={11} />
              {DIRECTION_SHORT[item.direction] || item.direction}
            </span>
            {managed && (
              <span className="pm-tag api">
                <Plug size={11} />
                {API_PROVIDER_LABELS[item.provider]}
              </span>
            )}
            {item.showcase_only && (
              <span className="pm-tag showcase">
                <EyeOff size={11} />
                Göstermelik
              </span>
            )}
          </div>
        </div>
        <div className="pm-row-account">
          {account.main}
          <small className={account.mono ? 'pm-mono' : ''}>{account.sub}</small>
        </div>
        <div className="pm-row-limit">
          {money(item.min_amount)} – {money(item.max_amount)}
        </div>
        <div className="pm-row-tools">
          <button
            type="button"
            className={`pm-switch ${item.active ? 'is-on' : ''}`}
            onClick={() => !managed && toggle(item)}
            disabled={managed}
            title={managed
              ? `${API_PROVIDER_LABELS[item.provider]} yöntemi Finans ayarlarından açılıp kapanır`
              : (item.active ? 'Yöntemi kapat' : 'Yöntemi aç')}
          >
            <span className="pm-switch-track" aria-hidden="true" />
            {item.active ? 'Aktif' : 'Pasif'}
          </button>
          {!managed && manualGroup.length > 1 && (
            <div className="pm-order" role="group" aria-label={`${item.name} sırası`}>
              <button
                type="button"
                onClick={() => moveMethod(group.id, item.id, -1)}
                disabled={manualIndex <= 0 || Boolean(ordering)}
                title="Yukarı taşı"
                aria-label={`${item.name} yöntemini yukarı taşı`}
              >
                <ArrowUp size={13} />
              </button>
              <button
                type="button"
                onClick={() => moveMethod(group.id, item.id, 1)}
                disabled={manualIndex >= manualGroup.length - 1 || Boolean(ordering)}
                title="Aşağı taşı"
                aria-label={`${item.name} yöntemini aşağı taşı`}
              >
                <ArrowDown size={13} />
              </button>
            </div>
          )}
          <button
            type="button"
            className="pm-tool"
            onClick={() => openEdit(item)}
            title="Düzenle"
            aria-label={`${item.name} yöntemini düzenle`}
          >
            <Edit3 size={15} />
          </button>
          {!managed && (
            <button
              type="button"
              className="pm-tool danger"
              onClick={() => remove(item)}
              title="Sil"
              aria-label={`${item.name} yöntemini sil`}
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const previewBrand = resolvePaymentBrand(form);

  return (
    <div className="admin-page pm-page">
      <header className="pm-hero">
        <span className="pm-hero-mark"><Landmark size={23} /></span>
        <div className="pm-hero-copy">
          <h1>Ödeme Yöntemleri</h1>
          <p>
            Para yatırma ve çekme yöntemleri oyuncuya burada tanımlanan
            bölümlerde görünür. Kripto yöntemleri aynı zamanda üyenin çekim
            hesabında seçebileceği para birimi ve ağ listesini oluşturur.
          </p>
        </div>
        <div className="pm-hero-actions">
          <button type="button" className="admin-btn admin-btn-secondary" onClick={load}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Yenile
          </button>
          <button type="button" className="admin-btn admin-btn-primary" onClick={openNew}>
            <Plus size={16} />
            Yeni Yöntem
          </button>
        </div>
      </header>

      {error && <AdminError message={error} onRetry={load} />}
      {loading && <AdminSkeleton rows={5} />}

      {!loading && !error && items.length === 0 && (
        <AdminEmpty
          title="Ödeme yöntemi yok"
          description="İlk para yatırma veya çekme yöntemini ekleyin."
        />
      )}

      {!loading && !error && items.length > 0 && (
        <>
          <div className="pm-stats">
            <div className="pm-stat">
              <span className="pm-stat-icon accent"><Landmark size={17} /></span>
              <div className="pm-stat-body">
                <strong>{stats.total}</strong>
                <span>Toplam yöntem</span>
              </div>
            </div>
            <div className="pm-stat">
              <span className="pm-stat-icon green"><ShieldCheck size={17} /></span>
              <div className="pm-stat-body">
                <strong>{stats.active}</strong>
                <span>Yayında</span>
              </div>
            </div>
            <div className="pm-stat">
              <span className="pm-stat-icon"><ArrowDownToLine size={17} /></span>
              <div className="pm-stat-body">
                <strong>{stats.deposit}</strong>
                <span>Para yatırma</span>
              </div>
            </div>
            <div className="pm-stat">
              <span className="pm-stat-icon"><ArrowUpFromLine size={17} /></span>
              <div className="pm-stat-body">
                <strong>{stats.withdrawal}</strong>
                <span>Para çekme</span>
              </div>
            </div>
            <div className="pm-stat">
              <span className="pm-stat-icon blue"><Plug size={17} /></span>
              <div className="pm-stat-body">
                <strong>{stats.api}</strong>
                <span>API sağlayıcısı</span>
              </div>
            </div>
            {stats.showcase > 0 && (
              <div className="pm-stat is-warn">
                <span className="pm-stat-icon amber"><EyeOff size={17} /></span>
                <div className="pm-stat-body">
                  <strong>{stats.showcase}</strong>
                  <span>Göstermelik firma</span>
                </div>
              </div>
            )}
          </div>

          <div className="pm-toolbar">
            <div className="pm-toolbar-row">
              <div className="pm-seg" role="group" aria-label="Kullanım alanı süzgeci">
                {[
                  { id: 'all', label: 'Tümü', icon: SlidersHorizontal },
                  { id: 'deposit', label: 'Para Yatırma', icon: ArrowDownToLine },
                  { id: 'withdrawal', label: 'Para Çekme', icon: ArrowUpFromLine },
                ].map((option) => {
                  const OptionIcon = option.icon;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      aria-pressed={directionFilter === option.id}
                      onClick={() => setDirectionFilter(option.id)}
                    >
                      <OptionIcon size={14} />
                      {option.label}
                    </button>
                  );
                })}
              </div>

              <div className="pm-search">
                <Search size={15} className="pm-search-icon" aria-hidden="true" />
                <input
                  type="search"
                  className="admin-input pm-search-input"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Yöntem, banka, IBAN, ağ veya sağlayıcı ara…"
                  aria-label="Ödeme yöntemi ara"
                />
                {search && (
                  <button
                    type="button"
                    className="pm-search-clear"
                    onClick={() => setSearch('')}
                    aria-label="Aramayı temizle"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="pm-views" role="group" aria-label="Görünüm">
                <button
                  type="button"
                  aria-pressed={view === 'grid'}
                  onClick={() => chooseView('grid')}
                  title="Kart görünümü"
                >
                  <LayoutGrid size={15} />
                </button>
                <button
                  type="button"
                  aria-pressed={view === 'list'}
                  onClick={() => chooseView('list')}
                  title="Liste görünümü"
                >
                  <Rows3 size={15} />
                </button>
              </div>
            </div>

            <div className="pm-toolbar-row">
              <div className="pm-chips" role="group" aria-label="Durum süzgeci">
                {[
                  { id: 'all', label: 'Hepsi', count: stats.total },
                  { id: 'active', label: 'Aktif', count: stats.active },
                  { id: 'passive', label: 'Pasif', count: stats.passive },
                  { id: 'api', label: 'API', count: stats.api },
                  { id: 'manual', label: 'Manuel', count: stats.manual },
                  { id: 'showcase', label: 'Göstermelik', count: stats.showcase },
                ].map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className="pm-chip"
                    aria-pressed={statusFilter === chip.id}
                    onClick={() => setStatusFilter(chip.id)}
                  >
                    {chip.label}
                    <span className="pm-chip-count">{chip.count}</span>
                  </button>
                ))}
              </div>
              {filtersActive && (
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={clearFilters}
                  style={{ marginLeft: 'auto' }}
                >
                  <X size={15} />
                  Süzgeçleri temizle
                </button>
              )}
            </div>
          </div>

          {groups.length === 0 ? (
            <div className="pm-empty">
              Süzgece uyan ödeme yöntemi yok. Aramayı veya süzgeçleri değiştirin.
            </div>
          ) : (
            <div className="pm-groups">
              {groups.map((group) => {
                const GroupIcon = group.icon || Landmark;
                const open = !closedGroups.has(group.id);
                const activeCount = group.methods.filter((item) => item.active).length;
                return (
                  <section
                    key={group.id}
                    className={`pm-group ${open ? 'is-open' : ''}`}
                    style={{ '--pm-accent': group.accent }}
                  >
                    <button
                      type="button"
                      className="pm-group-head"
                      onClick={() => toggleGroup(group.id)}
                      aria-expanded={open}
                    >
                      <span className="pm-group-icon"><GroupIcon size={17} /></span>
                      <span className="pm-group-title">
                        <strong>{group.name}</strong>
                        <span>
                          {group.hint}
                          {' · '}
                          {activeCount} yayında
                          {activeCount !== group.methods.length
                            && `, ${group.methods.length - activeCount} kapalı`}
                        </span>
                      </span>
                      <span className="pm-group-count">
                        {group.methods.length}
                        {group.methods.length !== group.totalInCategory
                          && ` / ${group.totalInCategory}`}
                      </span>
                      <ChevronDown size={17} className="pm-group-caret" />
                    </button>
                    {open && (
                      <div className="pm-group-body">
                        {view === 'grid' ? (
                          <div className="pm-grid">
                            {group.methods.map((item) => renderCard(item, group))}
                          </div>
                        ) : (
                          <div className="pm-rows">
                            {group.methods.map((item) => renderRow(item, group))}
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}

      {editing && (
        <div className="pm-modal-scrim" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label="Kapat"
            className="pm-modal-veil"
            onClick={() => setEditing(null)}
          />
          <form onSubmit={save} className="pm-modal">
            <header className="pm-modal-head">
              <PaymentMethodLogo method={form} size={42} />
              <div className="pm-modal-head-copy">
                <h2>
                  {editing.mode === 'new'
                    ? 'Yeni ödeme yöntemi'
                    : (form.name || 'Ödeme yöntemini düzenle')}
                </h2>
                <p>
                  {isCrypto ? 'Kripto para' : (isWallet ? 'Sağlayıcı cüzdanı' : 'Banka / IBAN')}
                  {' · '}
                  {DIRECTION_LABELS[form.direction]}
                  {isShowcase && ' · Göstermelik firma'}
                </p>
              </div>
              <button
                type="button"
                className="pm-tool"
                onClick={() => setEditing(null)}
                aria-label="Pencereyi kapat"
              >
                <X size={17} />
              </button>
            </header>

            <div className="pm-modal-body">
              <div className="pm-form">
                {formApiManaged && (
                  <div className="pm-note info">
                    <Plug size={15} />
                    <span>
                      Bu satırı <b>{API_PROVIDER_LABELS[form.provider]}</b> üretir.
                      Ad, limitler, sıra, talimat ve aktiflik her ayar kaydında
                      sağlayıcı entegrasyonundan yeniden yazılır — buradan
                      kalıcı olarak değiştirilebilen tek şey <b>logodur</b>.
                      Kalanını <b>Ayarlar → Finans</b> sayfasından yönetin.
                    </span>
                  </div>
                )}

                <section className="pm-section">
                  <header>
                    <h3><Landmark size={13} /> Yöntem türü</h3>
                    <p>
                      {isWallet
                        ? 'Sağlayıcı cüzdanı. Tür değiştirilemez: hedef bir IBAN değil cüzdan numarasıdır ve satırı sağlayıcı üretir.'
                        : 'Yöntemin hangi hesap bilgisini taşıyacağını belirler.'}
                    </p>
                  </header>
                  <div className="pm-types">
                    {isWallet ? (
                      <button type="button" className="pm-type" aria-pressed="true" disabled>
                        <span className="pm-type-icon"><Wallet size={18} /></span>
                        <span className="pm-type-copy">
                          <strong>Sağlayıcı Cüzdanı</strong>
                          <small>Cüzdan numarası işlem anında alınır</small>
                        </span>
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="pm-type"
                          aria-pressed={!isCrypto}
                          disabled={formApiManaged}
                          onClick={() => chooseType('bank')}
                        >
                          <span className="pm-type-icon"><Landmark size={18} /></span>
                          <span className="pm-type-copy">
                            <strong>Banka / IBAN</strong>
                            <small>Havale, EFT, FAST, kart</small>
                          </span>
                        </button>
                        <button
                          type="button"
                          className="pm-type"
                          aria-pressed={isCrypto}
                          disabled={formApiManaged}
                          onClick={() => chooseType('crypto')}
                        >
                          <span className="pm-type-icon"><Bitcoin size={18} /></span>
                          <span className="pm-type-copy">
                            <strong>Kripto Para</strong>
                            <small>Para birimi ve ağ seçilir</small>
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </section>

                {isCrypto && (
                  <section className="pm-section">
                    <header>
                      <h3><Bitcoin size={13} /> Kripto bilgileri</h3>
                      <p>Para birimi listeden seçilir, ikon otomatik gelir. Ağ serbest metindir.</p>
                    </header>
                    <div className="pm-fields">
                      <label className="pm-field">
                        <span>Para birimi</span>
                        <div className="pm-asset-row">
                          <PaymentMethodLogo
                            method={{ method_type: 'crypto', asset: form.asset }}
                            size={38}
                          />
                          <select
                            required
                            className="admin-input"
                            value={form.asset}
                            disabled={formApiManaged}
                            onChange={(event) => chooseAsset(event.target.value)}
                          >
                            <option value="" disabled>Para birimi seçin</option>
                            {CRYPTO_ASSETS.map((asset) => (
                              <option key={asset.symbol} value={asset.symbol}>
                                {asset.name} ({asset.symbol})
                              </option>
                            ))}
                            {form.asset && !findCryptoAsset(form.asset) && (
                              <option value={form.asset}>{form.asset}</option>
                            )}
                          </select>
                        </div>
                      </label>
                      <label className="pm-field">
                        <span>Ağ</span>
                        <input
                          required
                          list="crypto-network-suggestions"
                          className="admin-input pm-mono-input"
                          value={form.network}
                          disabled={formApiManaged}
                          onChange={(event) => setForm({
                            ...form, network: event.target.value.toUpperCase(),
                          })}
                          placeholder="TRC20, ERC20, BEP20…"
                        />
                        <datalist id="crypto-network-suggestions">
                          {assetNetworks.map((network) => (
                            <option key={network} value={network} />
                          ))}
                        </datalist>
                        <small>
                          {assetNetworks.length
                            ? `Öneriler: ${assetNetworks.join(', ')}`
                            : 'Sağlayıcınızın kullandığı ağ kodunu yazın.'}
                        </small>
                      </label>
                    </div>
                  </section>
                )}

                <section className="pm-section">
                  <header>
                    <h3><Info size={13} /> Temel bilgiler</h3>
                  </header>
                  <div className="pm-fields">
                    <label className="pm-field">
                      <span>Yöntem adı</span>
                      <input
                        required
                        className="admin-input"
                        value={form.name}
                        disabled={formApiManaged}
                        onChange={(event) => {
                          // Alan boşaltılırsa ad yeniden para birimini izler.
                          setNameTouched(Boolean(event.target.value.trim()));
                          setForm({ ...form, name: event.target.value });
                        }}
                        placeholder={isCrypto ? 'Tether (USDT)' : 'Havale / EFT'}
                      />
                      <small>
                        {formApiManaged
                          ? 'Sağlayıcı entegrasyonundan gelir.'
                          : (isCrypto && !nameTouched
                            ? 'Para birimini takip eder; yazarsanız sabitlenir.'
                            : 'Üyeye görünen isim.')}
                      </small>
                    </label>
                    <label className="pm-field">
                      <span>Kullanım alanı</span>
                      <select
                        className="admin-input"
                        value={form.direction}
                        disabled={formApiManaged}
                        onChange={(event) => setForm({
                          ...form, direction: event.target.value,
                        })}
                      >
                        <option value="deposit">Para Yatırma</option>
                        <option value="withdrawal">Para Çekme</option>
                        <option value="both">Yatırma ve Çekme</option>
                      </select>
                    </label>
                    <label className="pm-field">
                      <span>Sıra</span>
                      <input
                        type="number"
                        min="0"
                        className="admin-input"
                        value={form.order}
                        disabled={formApiManaged}
                        onChange={(event) => setForm({
                          ...form, order: Number(event.target.value),
                        })}
                      />
                      <small>
                        {formApiManaged
                          ? 'Sağlayıcı senkronu her yazımda yeniden hesaplar.'
                          : 'Küçük değer bölüm içinde önce görünür.'}
                      </small>
                    </label>
                    <label className="pm-field">
                      <span>İşlem sağlayıcısı</span>
                      <input
                        className="admin-input"
                        value={formApiManaged
                          ? API_PROVIDER_LABELS[form.provider]
                          : 'Manuel (hesap panelde tanımlı)'}
                        disabled
                        readOnly
                      />
                    </label>
                  </div>
                </section>

                {formApiManaged ? (
                  <section className="pm-section">
                    <header>
                      <h3><ImageIcon size={13} /> Logo</h3>
                      <p>
                        {isWallet
                          ? `Ödeme hedefi yöntem satırında durmaz: yatırımda ${API_PROVIDER_LABELS[form.provider]} her talepte kendi ödeme sayfasını verir, çekimde cüzdan numarasını üye yazar.`
                          : `Hesap bilgileri (banka, hesap sahibi, IBAN, kripto adresi) her talepte ${API_PROVIDER_LABELS[form.provider]} tarafından sağlanır.`}
                      </p>
                    </header>
                    {iconPicker('Varsayılan ikon kullanılıyor. Sağlayıcının logosunu yüklemek isterseniz açın.')}
                  </section>
                ) : isShowcase ? (
                  <section className="pm-section">
                    <header>
                      <h3><EyeOff size={13} /> Hesap bilgileri</h3>
                    </header>
                    <div className="pm-note warn">
                      <EyeOff size={15} />
                      <span>
                        <b>Göstermelik firma:</b> bu yöntemin arkasında hesap
                        yoktur, bu yüzden banka ve cüzdan bilgisi sorulmaz.
                        Yöntem listede adı, logosu ve limitleriyle görünür; üye
                        {form.direction === 'withdrawal'
                          ? ' tutarı girip çekim talebi oluşturduğunda'
                          : form.direction === 'both'
                            ? ' yatırımda ödeme adımına geçtiğinde, çekimde talep oluşturduğunda'
                            : ' tutarı girip ödeme adımına geçtiğinde'}
                        {' '}
                        aşağıdaki uyarı mesajını görür ve <b>talep oluşmaz</b>.
                      </span>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      {iconPicker('Varsayılan ikon kullanılıyor. Firmanın logosunu yüklemek isterseniz açın.')}
                    </div>
                  </section>
                ) : isCrypto ? (
                  <section className="pm-section">
                    <header>
                      <h3><Bitcoin size={13} /> Alıcı cüzdan</h3>
                    </header>
                    {form.direction === 'withdrawal' ? (
                      <div className="pm-note">
                        <Info size={15} />
                        <span>
                          Bu yöntem yalnızca çekim içindir; alıcı adresi üye
                          kendi hesabından girer. Seçtiğiniz para birimi ve ağ,
                          üyenin çekim hesabı ekranında seçenek olarak görünür.
                        </span>
                      </div>
                    ) : (
                      <div className="pm-fields one">
                        <label className="pm-field">
                          <span>Yatırım alıcı cüzdan adresi</span>
                          <input
                            required
                            className="admin-input pm-mono-input"
                            value={form.wallet_address}
                            onChange={(event) => setForm({
                              ...form, wallet_address: event.target.value.trim(),
                            })}
                            placeholder="T… / 0x…"
                          />
                          <small>
                            Yatırımların geleceği site cüzdanı.
                            {form.direction === 'both'
                              && ' Para çekmede üye kendi adresini girer; bu adres yalnızca yatırım içindir.'}
                          </small>
                        </label>
                      </div>
                    )}
                  </section>
                ) : (
                  <section className="pm-section">
                    <header>
                      <h3><Landmark size={13} /> Banka bilgileri</h3>
                    </header>
                    <div className="pm-fields">
                      <label className="pm-field">
                        <span>Banka adı</span>
                        <input
                          className="admin-input"
                          value={form.bank_name}
                          onChange={(event) => setForm({
                            ...form, bank_name: event.target.value,
                          })}
                          placeholder="Ziraat Bankası"
                        />
                      </label>
                      {form.direction === 'withdrawal' ? (
                        <div className="pm-field">
                          <span>Hesap</span>
                          <div className="pm-note">
                            <Info size={15} />
                            <span>Üye çekim sırasında kendi IBAN&apos;ını girecek.</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <label className="pm-field">
                            <span>Hesap sahibi</span>
                            <input
                              required
                              className="admin-input"
                              value={form.account_holder}
                              onChange={(event) => setForm({
                                ...form, account_holder: event.target.value,
                              })}
                              placeholder="Ad Soyad"
                            />
                          </label>
                          <label className="pm-field wide">
                            <span>IBAN</span>
                            <input
                              required
                              className="admin-input pm-mono-input"
                              style={{ textTransform: 'uppercase' }}
                              value={form.iban}
                              onChange={(event) => setForm({
                                ...form, iban: event.target.value,
                              })}
                              placeholder="TR.. .... .... .... .... .... .."
                            />
                          </label>
                        </>
                      )}
                    </div>
                    <div style={{ marginTop: 12 }}>
                      {iconPicker('Varsayılan banka ikonu kullanılıyor. Bankanın logosunu yüklemek isterseniz açın.')}
                    </div>
                  </section>
                )}

                <section className="pm-section">
                  <header>
                    <h3><ListOrdered size={13} /> Limitler</h3>
                    <p>Üyenin tek işlemde girebileceği en düşük ve en yüksek tutar.</p>
                  </header>
                  <div className="pm-fields">
                    <label className="pm-field">
                      <span>Minimum tutar (₺)</span>
                      <input
                        required
                        type="number"
                        min="0"
                        step="0.01"
                        className="admin-input"
                        value={form.min_amount}
                        disabled={formApiManaged}
                        onChange={(event) => setForm({
                          ...form, min_amount: Number(event.target.value),
                        })}
                      />
                    </label>
                    <label className="pm-field">
                      <span>Maksimum tutar (₺)</span>
                      <input
                        required
                        type="number"
                        min="0.01"
                        step="0.01"
                        className="admin-input"
                        value={form.max_amount}
                        disabled={formApiManaged}
                        onChange={(event) => setForm({
                          ...form, max_amount: Number(event.target.value),
                        })}
                      />
                    </label>
                    <div className="pm-field wide">
                      {isShowcase ? (
                        // Göstermelik yöntemde ödeme sayfasına hiç geçilmez,
                        // yani "kullanıcı talimatı"nın çizileceği bir yer
                        // yoktur; onun yerine üyenin göreceği uyarı yazılır.
                        <label className="pm-field">
                          <span>Uyarı mesajı</span>
                          <textarea
                            rows={3}
                            maxLength={500}
                            className="admin-input"
                            value={form.showcase_message || ''}
                            onChange={(event) => setForm({
                              ...form, showcase_message: event.target.value,
                            })}
                            placeholder={SHOWCASE_DEFAULT_MESSAGE}
                          />
                          <small>
                            Üye bu yöntemle işlem yapmaya çalıştığında gördüğü
                            metin. Boş bırakılırsa: &laquo;{SHOWCASE_DEFAULT_MESSAGE}&raquo;
                          </small>
                        </label>
                      ) : (
                        <label className="pm-field">
                          <span>Kullanıcı talimatı</span>
                          <textarea
                            rows={3}
                            className="admin-input"
                            value={form.instructions}
                            disabled={formApiManaged}
                            onChange={(event) => setForm({
                              ...form, instructions: event.target.value,
                            })}
                            placeholder="Açıklama alanına kullanıcı adınızı yazın…"
                          />
                          <small>Ödeme adımında hesap bilgisinin altında görünür.</small>
                        </label>
                      )}
                    </div>
                  </div>
                </section>

                <section className="pm-section">
                  <header>
                    <h3><ShieldCheck size={13} /> Yayın durumu</h3>
                  </header>
                  <div className="pm-toggles">
                    <label className={`pm-toggle ${form.active ? 'is-on' : ''} ${formApiManaged ? 'disabled' : ''}`}>
                      <input
                        type="checkbox"
                        checked={Boolean(form.active)}
                        disabled={formApiManaged}
                        onChange={(event) => setForm({
                          ...form, active: event.target.checked,
                        })}
                      />
                      <span className="pm-toggle-copy">
                        <strong><Eye size={14} /> Yöntem yayında</strong>
                        <small>
                          {formApiManaged
                            ? 'Bu yöntem Ayarlar → Finans sayfasından açılıp kapanır.'
                            : 'Kapalıyken üye yöntemi listede hiç görmez; geçmiş işlemler etkilenmez.'}
                        </small>
                      </span>
                    </label>

                    {!formApiManaged && (
                      <label className={`pm-toggle warn ${isShowcase ? 'is-on' : ''}`}>
                        <input
                          type="checkbox"
                          checked={isShowcase}
                          onChange={(event) => chooseShowcase(event.target.checked)}
                        />
                        <span className="pm-toggle-copy">
                          <strong><EyeOff size={14} /> Göstermelik firma</strong>
                          <small>
                            Yöntem vitrinde durur ama arkasında hesap yoktur.
                            Banka ve cüzdan alanları sorulmaz; üye işlem yapmaya
                            çalıştığında uyarı görür ve talep oluşmaz.
                          </small>
                        </span>
                      </label>
                    )}
                  </div>
                </section>
              </div>

              <aside className="pm-aside">
                <span className="pm-preview-label">
                  <Eye size={12} /> Üyenin göreceği satır
                </span>
                <div
                  className="pm-preview"
                  style={{ '--pm-accent': previewBrand.accent }}
                >
                  <PaymentMethodLogo method={form} size={38} />
                  <div className="pm-preview-copy">
                    <strong>{form.name || 'Yöntem adı'}</strong>
                    <span>{money(form.min_amount)} – {money(form.max_amount)}</span>
                  </div>
                </div>

                <dl className="pm-summary">
                  <div className="pm-summary-row">
                    <dt>Bölüm</dt>
                    <dd>
                      {DEPOSIT_CATEGORIES.find(
                        (category) => category.id === categoryOf(form),
                      )?.name || '—'}
                    </dd>
                  </div>
                  <div className="pm-summary-row">
                    <dt>Kullanım</dt>
                    <dd>{DIRECTION_LABELS[form.direction]}</dd>
                  </div>
                  <div className="pm-summary-row">
                    <dt>Tür</dt>
                    <dd>
                      {isCrypto
                        ? `${form.asset || 'Kripto'}${form.network ? ` · ${form.network}` : ''}`
                        : (isWallet ? 'Sağlayıcı cüzdanı' : 'Banka / IBAN')}
                    </dd>
                  </div>
                  <div className="pm-summary-row">
                    <dt>Kaynak</dt>
                    <dd>
                      {formApiManaged
                        ? API_PROVIDER_LABELS[form.provider]
                        : (isShowcase ? 'Göstermelik' : 'Manuel')}
                    </dd>
                  </div>
                  <div className="pm-summary-row">
                    <dt>Durum</dt>
                    <dd>{form.active ? 'Yayında' : 'Kapalı'}</dd>
                  </div>
                </dl>

                <div className="pm-note">
                  <Info size={15} />
                  <span>
                    Bölüm yöntemin türünden ve sağlayıcısından <b>okuma anında</b>
                    {' '}çözülür; oyuncu yöntemi para yatırma ekranında bu
                    sekmenin altında görür.
                  </span>
                </div>
              </aside>
            </div>

            <footer className="pm-modal-foot">
              <span className="pm-foot-hint">
                {editing.mode === 'new'
                  ? 'Yeni yöntem kaydedildikten sonra listede görünür.'
                  : 'Değişiklikler kaydedilince üye ekranına anında yansır.'}
              </span>
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => setEditing(null)}
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={busy}
                className="admin-btn admin-btn-primary"
              >
                <Save size={16} />
                {busy ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
};

export default PaymentMethods;
